<mat-card appearance="outlined" [class.decl-embedded]="embedded()">
  <mat-card-content>
    @if (isLoading()) {
      <p class="muted">Loading…</p>
    } @else if (loadError()) {
      <div class="panel-error">
        <p>Couldn't load these lists.</p>
        <button mat-stroked-button type="button" (click)="load()" data-testid="decl-retry-btn">
          Try again
        </button>
      </div>
    } @else {
      <!--
        ===== Declared bank accounts (FLOW-280) =====
        Ops mode only: the client's accounts surface is the wizard's bank step,
        and a second client-side list here would be a second place to declare
        the same thing. Ops release the declared-account hold on the strength of
        these rows, so this is where they become visible.
      -->
      @if (showsAccounts()) {
        <section class="decl" data-testid="decl-section-ACCOUNT">
          <div class="decl__head">
            <div>
              @if (!embedded()) {
                <h3 class="section-title">Bank accounts</h3>
              }
              <p class="muted decl__lead">
                The accounts the business has declared, and whether it has confirmed the list is
                complete.
              </p>
            </div>
            @if (!readOnly()) {
              <button
                mat-stroked-button
                type="button"
                (click)="openAddAccount()"
                data-testid="decl-add-toggle-ACCOUNT"
              >
                Add an account
              </button>
            }
          </div>

          <!--
            The affirmation, rendered as a RECORDED STATE and never as "now"
            (UI-DA-58-04): who said it, when, and — where it no longer stands —
            which later act retracted it and when. A bare "confirmed ✓" would
            assert a current system state over a record that is nothing of the
            kind.
          -->
          <div class="decl__affirm" data-testid="decl-affirmation">
            @if (affirmationInForce(); as a) {
              <p class="decl__affirm-line" data-testid="decl-affirmation-in-force">
                <app-icon name="check" [size]="16" />
                <span> Confirmed complete by {{ a.by }} on {{ a.at | date: 'mediumDate' }}. </span>
              </p>
            } @else if (affirmationRetracted(); as r) {
              <p class="decl__affirm-line" data-testid="decl-affirmation-retracted">
                <app-icon name="info" [size]="16" />
                <span>
                  No longer confirmed. {{ r.by }} confirmed the list on
                  {{ r.at | date: 'mediumDate' }}; that confirmation was retracted by
                  {{ r.retractedBy }} on {{ r.retractedAt | date: 'mediumDate' }}.
                </span>
              </p>
            } @else {
              <p class="decl__affirm-line" data-testid="decl-affirmation-none">
                <app-icon name="info" [size]="16" />
                <span>
                  The business has not confirmed that this is every account it operates.
                </span>
              </p>
            }
          </div>

          @if (liveAccounts().length === 0) {
            <p class="muted decl__empty" data-testid="decl-empty-ACCOUNT">
              No bank accounts declared yet.
            </p>
          } @else {
            <ul class="decl__list">
              @for (row of liveAccounts(); track row.short_id) {
                <li class="decl__row" [attr.data-testid]="'decl-row-' + row.short_id">
                  <div class="decl__row-main">
                    <span class="decl__row-title">{{ accountLabel(row) }}</span>
                    <span class="muted decl__row-detail"
                      >Holder: {{ accountHolder(row) | dash }}</span
                    >
                    @if (clientHolderNote(row); as stated) {
                      <span class="muted decl__row-detail">Client says held by: {{ stated }}</span>
                    }
                    <span class="muted decl__row-detail"
                      >Trading goes through it: {{ tradingLabel(row) }}</span
                    >
                    <span class="muted decl__row-detail"
                      >Principal trading account: {{ principalLabel(row) }}</span
                    >
                  </div>
                  <div class="decl__row-meta">
                    <span class="muted"
                      >{{ row.declared_by_name }} · {{ row.declared_at | date: 'mediumDate' }}</span
                    >
                    <!--
                      Always rendered, both ways round: an account the client
                      declared and one CapX added from a statement are different
                      acts, and tagging only the second leaves the first as the
                      unlabelled default.
                    -->
                    <span class="decl__tag" [attr.data-testid]="'decl-source-' + row.short_id">{{
                      sourceLabel(row)
                    }}</span>
                    @if (!readOnly()) {
                      <button
                        mat-button
                        type="button"
                        [disabled]="withdrawingId() === row.short_id"
                        (click)="confirmWithdraw(row)"
                        [attr.data-testid]="'decl-withdraw-' + row.short_id"
                      >
                        Withdraw
                      </button>
                    }
                  </div>
                </li>
              }
            </ul>
          }

          @if (withdrawnAccounts().length > 0) {
            <button
              mat-button
              type="button"
              class="decl__withdrawn-toggle"
              (click)="toggleWithdrawn()"
              data-testid="decl-withdrawn-toggle-ACCOUNT"
            >
              <app-icon name="info" [size]="16" />
              <span>
                {{ showWithdrawn() ? 'Hide' : 'Show' }} {{ withdrawnAccounts().length }} withdrawn
              </span>
            </button>
            @if (showWithdrawn()) {
              <ul class="decl__list decl__list--withdrawn">
                @for (row of withdrawnAccounts(); track row.short_id) {
                  <li class="decl__row" [attr.data-testid]="'decl-withdrawn-' + row.short_id">
                    <span class="decl__row-title">{{ accountLabel(row) }}</span>
                    <span class="muted">Withdrawn</span>
                  </li>
                }
              </ul>
            }
          }
        </section>
      }

      @for (spec of specs(); track spec.kind) {
        <section class="decl" [attr.data-testid]="'decl-section-' + spec.kind">
          <div class="decl__head">
            <div>
              @if (!embedded()) {
                <h3 class="section-title">{{ spec.title }}</h3>
              }
              <p class="muted decl__lead">{{ spec.lead }}</p>
            </div>
            @if (!readOnly()) {
              <button
                mat-stroked-button
                type="button"
                (click)="openAdd(spec.kind)"
                [attr.data-testid]="'decl-add-toggle-' + spec.kind"
              >
                {{ addLabel(spec) }}
              </button>
            }
          </div>

          @if (liveOf(spec.kind).length === 0) {
            <p class="muted decl__empty" [attr.data-testid]="'decl-empty-' + spec.kind">
              {{ spec.emptyText }}
            </p>
          } @else {
            <ul class="decl__list">
              @for (row of liveOf(spec.kind); track row.short_id) {
                <li class="decl__row" [attr.data-testid]="'decl-row-' + row.short_id">
                  <div class="decl__row-main">
                    <span class="decl__row-title">{{ labelFor(row) }}</span>
                    <span class="muted decl__row-detail">{{ detailFor(row) | dash }}</span>
                    @if (row.id_number) {
                      <span class="muted decl__row-detail">ID {{ row.id_number }}</span>
                    }
                    @if (row.payer_names?.length) {
                      <span class="muted decl__row-detail"
                        >Pays as: {{ row.payer_names?.join(', ') }}</span
                      >
                    }
                  </div>
                  <div class="decl__row-meta">
                    <span class="muted"
                      >{{ row.declared_by_name }} · {{ row.declared_at | date: 'mediumDate' }}</span
                    >
                    @if (row.declared_source === 'OPS_ADDED') {
                      <span class="decl__tag">Added by CapX</span>
                    }
                    @if (row.declared_source === 'CIPC') {
                      <span class="decl__tag" data-testid="confirmed-by-cipc">
                        Confirmed by CIPC — {{ row.declared_at | date: 'mediumDate' }}
                      </span>
                    }
                    <!-- FC-DET-111: on the business's and agent's screens a name
                         has no Correct or Withdraw; only CapX removes one. -->
                    @if (!readOnly() && !(mode() === 'client' && spec.kind === 'DIRECTOR')) {
                      <!--
                        "Correct", never "Edit" (N8): saving withdraws this entry
                        and declares the corrected one, and both stay on record.
                      -->
                      <button
                        mat-button
                        type="button"
                        [disabled]="withdrawingId() === row.short_id"
                        (click)="openCorrect(spec.kind, row)"
                        [attr.data-testid]="'decl-correct-' + row.short_id"
                      >
                        Correct
                      </button>
                      <button
                        mat-button
                        type="button"
                        [disabled]="withdrawingId() === row.short_id"
                        (click)="confirmWithdraw(row)"
                        [attr.data-testid]="'decl-withdraw-' + row.short_id"
                      >
                        Withdraw
                      </button>
                    }
                  </div>
                </li>
              }
            </ul>
          }

          @if (withdrawnOf(spec.kind).length > 0) {
            <button
              mat-button
              type="button"
              class="decl__withdrawn-toggle"
              (click)="toggleWithdrawn()"
              [attr.data-testid]="'decl-withdrawn-toggle-' + spec.kind"
            >
              <app-icon name="info" [size]="16" />
              <span>
                {{ showWithdrawn() ? 'Hide' : 'Show' }}
                {{ withdrawnOf(spec.kind).length }} withdrawn
              </span>
            </button>
            @if (showWithdrawn()) {
              <ul class="decl__list decl__list--withdrawn">
                @for (row of withdrawnOf(spec.kind); track row.short_id) {
                  <li class="decl__row" [attr.data-testid]="'decl-withdrawn-' + row.short_id">
                    <span class="decl__row-title">{{ labelFor(row) }}</span>
                    <span class="muted">Withdrawn</span>
                  </li>
                }
              </ul>
            }
          }
        </section>
      }

      @if (serverError(); as error) {
        <p class="panel-error" role="alert" data-testid="decl-error">{{ error }}</p>
      }
    }
  </mat-card-content>
</mat-card>
